import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Link, useNavigate } from 'react-router-dom'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import {
   Grid,
   TextField,
   Box,
   Typography,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Button,
   FormHelperText
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { API_URL } from '../../Config'
import { toast } from 'react-toastify'
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery'
import StyledDashboardLayout from '../style'
import { useGetProvidersQuery } from '../../Redux/Service/ProviderApi'
import API from '../../Web/Instance'
import { useGetCurrenciesQuery } from '../../Redux/Service/CurrencyApi'

const AddTechStackRate = () => {
   const navigate = useNavigate()
   const [techStacks, setTechStacks] = useState([])
   const [symbol, setSymbol] = useState('$')
   const [loading, setLoading] = useState(true)
   const {
      data: providerData,
      isLoading: isLoadingForProvider,
      isError: isErrorForProvider,
      error: errorForProvider,
   } = useGetProvidersQuery()

   React.useEffect(() => {
      API.get(`technology-stack/list-technology`).then((response) => {
         setTechStacks(response.data?.response)
      })
   }, [])

   // make loading false
   useEffect(() => {
      setLoading(false)
   }, [])

   const {
      data: currencies,
      isLoading: isLoadingForCurrency,
      error: errorForCurrency,
   } = useGetCurrenciesQuery()

   const currencySymbolSet = (currencyId, setFieldValue) => {
      setFieldValue('currencyId', currencyId)
      const selectedCurrency = currencies?.response?.find(
         (val) => val.id === currencyId,
      )
      setSymbol(selectedCurrency?.symbol ?? '$')
   }

   const initialValues = {
      serviceProviderId: '',
      techStackId: '',
      currencyId: '',
      client: '',
      hourlyRateText: '',
      description: '',
   }

   // Add validation schema for number fields
   const validationSchema = Yup.object({
      serviceProviderId: Yup.string().required('Service Provider is required'),
      techStackId: Yup.string().required('Tech Stack is required'),
      currencyId: Yup.string().required('Currency is required'),
      client: Yup.string().required('Client is required').max(50, 'Client should not exceed 50 characters'),
      hourlyRateText: Yup.number()
         .positive('Hourly rate must be a positive number')
         .required('Hourly rate is required')
         .max(99999, 'Hourly rate should not exceed 99999'),
      description: Yup.string().max(200, 'Description should not exceed 200 characters'),
   })

   // Update handleSubmit to ensure proper number conversion
   const handleSubmit = async (values, { setSubmitting }) => {
      try {
         const token = getAuthToken()

         // Convert string values to numbers before sending
         const payload = {
            ...values,
            hourlyRateText: parseFloat(values.hourlyRateText),
         }

         const response = await axios.post(
            `${API_URL}hourlyrate/create-hourly-techstack`,
            payload,
            {
               headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
               },
            },
         )
         console.log('Hourly Rate added successfully...', response)
         toast.success(response?.data?.message)
         navigate('/hourly-rate/tech-stack')
      } catch (error) {
         toast.error(
            error?.response?.data?.message || 'Failed to add hourly rate',
         )
      } finally {
         setSubmitting(false)
      }
   }

   if (loading || isLoadingForProvider) {
      return <div>Loading...</div>
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Add Tech Stack Hourly Rate</h1>
                     </div>
                     <div className="btnWrap">
                        <Link
                           to="/hourly-rate/tech-stack"
                           className="btn addBtn"
                        >
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>
            <div className="card customCard p-3 m-3">
               <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
               >
                  {({
                     values,
                     setFieldValue,
                     touched,
                     errors,
                     isSubmitting,
                     handleBlur,
                  }) => (
                     <Form>
                        <Box className="card-body" sx={{ padding: '15px 0' }}>
                           <Grid container spacing={2}>
                              <Grid item md={6} xs={12}>
                                 {isErrorForProvider ? (
                                    <div>
                                       Error: {errorForProvider?.message}
                                    </div>
                                 ) : (
                                    <FormControl fullWidth>
                                       <InputLabel id="service-provider-label">
                                          Service Provider*
                                       </InputLabel>
                                       <Field
                                          name="serviceProviderId"
                                          as={Select}
                                          labelId="service-provider-label"
                                          label="Service Provider"
                                          onBlur={handleBlur}
                                          error={
                                             touched.serviceProviderId && Boolean(errors.serviceProviderId)
                                          }
                                       // helperText={touched.serviceProviderId && errors.serviceProviderId}
                                       >
                                          <MenuItem value="" disabled>
                                             Select Service Provider
                                          </MenuItem>
                                          {providerData?.response?.map(
                                             (val, index) => (
                                                <MenuItem
                                                   key={index}
                                                   value={val.id}
                                                >
                                                   {val.name}
                                                </MenuItem>
                                             ),
                                          )}
                                       </Field>
                                       {/* <ErrorMessage
                                          name="serviceProviderId"
                                          className="error-message"
                                          component="div"
                                          style={{ color: 'red' }}
                                       /> */}
                                       {touched.serviceProviderId && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                          {touched.serviceProviderId && errors.serviceProviderId} </FormHelperText>)}
                                    </FormControl>
                                 )}
                              </Grid>
                              <Grid item xs={12} md={6}>
                                 <FormControl fullWidth>
                                    <InputLabel id="techstack-label">
                                       Tech Stack*
                                    </InputLabel>
                                    <Field
                                       name="techStackId"
                                       as={Select}
                                       labelId="techstack-label"
                                       label="Tech Stack"
                                       onBlur={handleBlur}
                                       error={
                                          touched.techStackId && Boolean(errors.techStackId)
                                       }
                                    // helperText={touched.techStackId && errors.techStackId}
                                    >
                                       <MenuItem value="" disabled>
                                          Select Tech Stack
                                       </MenuItem>
                                       {techStacks.map((ts) => (
                                          <MenuItem key={ts.id} value={ts.id}>
                                             {ts.technology}
                                          </MenuItem>
                                       ))}
                                    </Field>
                                    {/* <ErrorMessage
                                       name="techStackId"
                                       component="div"
                                       style={{ color: 'red' }}
                                    /> */}
                                    {touched.techStackId && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                       {touched.techStackId && errors.techStackId} </FormHelperText>)}
                                 </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                 <FormControl fullWidth>
                                    {/* <label htmlFor="CurrencyId">Currency</label> */}
                                    <InputLabel id="currency-label">
                                       Currency*
                                    </InputLabel>
                                    <Field
                                       label="Currency"
                                       name="currencyId"
                                       as={Select}
                                       labelId="currency-label"
                                       onChange={(event) =>
                                          currencySymbolSet(
                                             event.target.value,
                                             setFieldValue,
                                          )
                                       }
                                       error={
                                          touched.currencyId && Boolean(errors.currencyId)
                                       }
                                    // helperText={touched.currencyId && errors.currencyId}
                                    >
                                       {currencies?.response?.map(
                                          (val, index) => (
                                             <MenuItem
                                                key={index}
                                                value={val.id}
                                             >
                                                {val.name} ({val.code})
                                             </MenuItem>
                                          ),
                                       )}
                                    </Field>
                                    {touched.currencyId && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                       {touched.currencyId && errors.currencyId} </FormHelperText>)}
                                 </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                 <Field
                                    fullWidth
                                    label="Client*"
                                    name="client"
                                    as={TextField}
                                    variant="outlined"
                                    onBlur={handleBlur}
                                    error={
                                       touched.client && Boolean(errors.client)
                                    }
                                    helperText={touched.client && errors.client}
                                 />
                              </Grid>
                              {/* Updated Hourly Rate Text field as number input */}
                              <Grid item xs={12} md={6}>
                                 <Field
                                    fullWidth
                                    label={`Hourly Rate (${symbol})*`}
                                    name="hourlyRateText"
                                    as={TextField}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                       min: '0',
                                       step: '0.01',
                                    }}
                                    onBlur={handleBlur}
                                    error={
                                       touched.hourlyRateText &&
                                       Boolean(errors.hourlyRateText)
                                    }
                                    helperText={
                                       touched.hourlyRateText &&
                                       errors.hourlyRateText
                                    }
                                 />
                              </Grid>
                              <Grid item xs={12}>
                                 <Field
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    as={TextField}
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    onBlur={handleBlur}
                                    error={
                                       touched.description &&
                                       Boolean(errors.description)
                                    }
                                    helperText={
                                       touched.description && errors.description
                                    }
                                 />
                              </Grid>
                              <Grid item xs={12}>
                                 <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    className="commonBtn"
                                 >
                                    {isSubmitting ? 'Saving...' : 'Save'}
                                 </Button>
                              </Grid>
                           </Grid>
                        </Box>
                     </Form>
                  )}
               </Formik>
            </div>
         </div>
      </StyledDashboardLayout>
   )
}

export default AddTechStackRate
